import {
  AlignLeft,
  Calculator,
  Calendar,
  ChevronSelectorVertical,
  Clock,
  Cube01,
  CurrencyDollarCircle,
  Globe01,
  Hash01,
  LayoutGrid01,
  Link01,
  Link03,
  Mail01,
  Palette,
  Percent02,
  Phone01,
  Share07,
  TextInput,
  Toggle01Right,
  Translate01,
  Type01,
  UserPlus01,
} from '@untitledui/icons';
import type { FC, HTMLAttributes } from 'react';

import { LinkedInIcon } from './CustomIcons';

/**
 * Minimal type for icon selection - only requires fields actually used by the icon logic.
 */
export interface PropertyTypeForIcon {
  property_type_name?: string | null;
  field_type_name: string;
  icon?: string | null;
}

interface PropertyTypeIconProps {
  propertyType: PropertyTypeForIcon;
  className?: string;
}

/**
 * Icon component type for property type icons.
 */
export type PropertyIconComponent = FC<HTMLAttributes<HTMLOrSVGElement>>;

/**
 * Map of icon name strings (from the backend `icon` field) to icon components.
 */
const ICON_NAME_MAP: Record<string, PropertyIconComponent> = {
  UserPlus01,
  LinkedInIcon,
};

/**
 * Gets the icon component for a given property type.
 * Useful for contexts where you need the icon component reference
 * rather than a rendered element (e.g., passing to Input component).
 *
 * Resolution order:
 * 1. Explicit `icon` field from the property type schema (e.g., "UserPlus01")
 * 2. Fallback to field_type_name-based mapping
 *
 * Standard mappings by field_type_name:
 * - text → Type01
 * - textarea → AlignLeft
 * - email → Mail01
 * - phone_number → Phone01
 * - number → Hash01
 * - percentage → Percent02
 * - date → Calendar
 * - datetime → Clock
 * - domain → Globe01
 * - url → Link01
 * - currency → CurrencyDollarCircle
 * - multi_currency → CurrencyDollarCircle
 * - select → ChevronSelectorVertical
 * - association → Link03
 * - association_type → Link03
 * - web_source → Share07
 */
// eslint-disable-next-line react-refresh/only-export-components -- icon utility function co-located with icon component
export function getPropertyTypeIconComponent(
  propertyType: PropertyTypeForIcon
): PropertyIconComponent {
  // Check explicit icon field first
  if (propertyType.icon) {
    const mapped = ICON_NAME_MAP[propertyType.icon];
    if (mapped) {
      return mapped;
    }
  }

  switch (propertyType.field_type_name) {
    case 'text':
      return Type01;
    case 'textarea':
    case 'markdown':
      return AlignLeft;
    case 'email':
      return Mail01;
    case 'phone_number':
      return Phone01;
    case 'number':
      return Hash01;
    case 'percentage':
      return Percent02;
    case 'date':
      return Calendar;
    case 'datetime':
      return Clock;
    case 'domain':
      return Globe01;
    case 'url':
      return Link01;
    case 'currency':
    case 'multi_currency':
      return CurrencyDollarCircle;
    case 'select':
      return ChevronSelectorVertical;
    case 'timezone':
      return Clock;
    case 'language':
      return Translate01;
    case 'association':
    case 'association_type':
      return Link03;
    case 'manifest':
      return LayoutGrid01;
    case 'permission_group':
      return UserPlus01;
    case 'color':
      return Palette;
    case 'property_type':
      return TextInput;
    case 'web_source':
      return Share07;
    case 'formula':
      return Calculator;
    case 'boolean':
      return Toggle01Right;
    case 'entity_type':
      return Cube01;
    default:
      return Type01;
  }
}

/**
 * Centralized icon component for CRM property types.
 * Renders the appropriate icon based on property field type and format.
 * Uses getPropertyTypeIconComponent internally to avoid duplicate switch logic.
 */
export function PropertyTypeIcon({
  propertyType,
  className,
}: PropertyTypeIconProps) {
  const IconComponent = getPropertyTypeIconComponent(propertyType);
  return <IconComponent className={className} />;
}
